/**
 * Turning a logged run back into a scope you can run again.
 *
 * A run history row records the three things that made it — the scenario, the
 * input scope, and the result — but until now only the result was readable. The
 * scope was stored as three loose columns and nothing reassembled them, so
 * "give me the numbers from that run three weeks ago, on today's data" meant
 * remembering which pool was selected and setting it by hand.
 *
 * Pure, so the reconstruction is testable without an engine context.
 */

import type {
  EngineInputSource,
  PlanningRunHistoryEntry,
} from '../../../lib/planningEngineService';

/**
 * Rebuild the EngineInputSource a run was executed against.
 *
 * Returns null when the row cannot describe a scope on its own: every named
 * source type addresses a record by id, and a row missing that id is a
 * historical fact we cannot turn back into a selection. Callers say so rather
 * than silently restoring "All Live Items", which would quietly widen the
 * scope — the one direction a restore must never drift.
 */
export function inputSourceFromRun(
  entry: Pick<PlanningRunHistoryEntry, 'inputSourceType' | 'inputSourceId' | 'inputSourceName'>,
): EngineInputSource | null {
  if (entry.inputSourceType === 'live') return { type: 'live' };
  if (!entry.inputSourceId) return null;
  return {
    type: entry.inputSourceType,
    id: entry.inputSourceId,
    name: entry.inputSourceName ?? '',
  } as EngineInputSource;
}

/** True when the engine is already scoped exactly as this run was. */
export function isCurrentScope(
  current: EngineInputSource,
  entry: Pick<PlanningRunHistoryEntry, 'inputSourceType' | 'inputSourceId'>,
): boolean {
  if (current.type !== entry.inputSourceType) return false;
  if (current.type === 'live') return true;
  return 'id' in current && current.id === entry.inputSourceId;
}

/**
 * Which logged runs are the active result for their scope right now.
 *
 * The active result of a scope is its planning_runs instance row. A logged run
 * is that result when it was saved into that row and carries the same run
 * time — save_planning_run (756) writes both with one timestamp, and
 * restore_planning_run (769) copies the logged run's time back onto the row.
 */
export function activeRunHistoryIds(
  entries: ReadonlyArray<Pick<PlanningRunHistoryEntry, 'id' | 'planningRunId' | 'runCompletedAt'>>,
  instances: ReadonlyArray<{ id: string; runCompletedAt: string | null }>,
): Set<string> {
  const byInstance = new Map(instances.map((r) => [r.id, r.runCompletedAt]));
  const active = new Set<string>();
  for (const e of entries) {
    if (!e.planningRunId || !e.runCompletedAt) continue;
    const instanceTime = byInstance.get(e.planningRunId);
    if (instanceTime && new Date(instanceTime).getTime() === new Date(e.runCompletedAt).getTime()) {
      active.add(e.id);
    }
  }
  return active;
}
